import {compute, Signal, signal} from "../../fjsc/src/signals.ts";
import {Album} from "../models/db/tri/Album.ts";
import {Api} from "../api/api.ts";
import {Generics} from "./generics.ts";
import {create, ifjs} from "../../fjsc/src/f2.ts";
import {Route} from "../routing/Route.ts";
import {Tab} from "../models/Tab.ts";
import {Tracks} from "./tracks.ts";
import {getImageUrl} from "../functions/templates.ts";
import {dayFrom} from "../functions/dates.ts";
import {MediaFileType} from "../enums/MediaFileType.ts";
import {RequestableImageSize} from "./requestableImageSize.ts";
import {Artists} from "./artists.ts";

const albums = signal<Album[]>([]);

export class Albums {
    static page() {
        const tabs: Tab[] = [
            {
                key: "albums",
                text: "Albums",
                icon: "album"
            },
            {
                key: "tracks",
                text: "Tracks",
                icon: "graphic_eq"
            }
        ];
        const tab$ = signal(tabs[0].key);

        return Generics.pageFrame(
            create("div")
                .classes("flex-v", "centered-content")
                .children(
                    Generics.heading(1, "Releases", ["bold"]),
                    Generics.tabSelector(tab$, tabs),
                    Generics.tabContents(tab$, {
                        "albums": () => Albums.albumsTab(),
                        "tracks": () => Tracks.tracksTab()
                    })
                ).build()
        );
    }

    static albumsTab() {
        const filter = signal("");
        const filteredAlbums = compute((a, f) => a.filter(a => {
            return a.title.toLowerCase().includes(f.toLowerCase()) ||
                a.artists.toLowerCase().includes(f.toLowerCase());
        }), albums, filter);
        const count = compute(a => a.length + " Albums", filteredAlbums);
        const loading = signal(false);
        Api.getAlbums()
            .then(a => albums.value = a)
            .finally(() => loading.value = false);

        return create("div")
            .classes("flex-v")
            .children(
                Generics.heading(2, count),
                ifjs(loading, Generics.loading()),
                Albums.albumsList(filteredAlbums)
            ).build();
    }

    static albumsList(filteredAlbums: Signal<Album[]>) {
        return Generics.table(
            ["", "Album title", "Artists", "Release date", "Tracks"],
            filteredAlbums,
            (album) => create("tr")
                .children(
                    create("td")
                        .children(
                            Generics.image(getImageUrl(MediaFileType.albumCover, album.id, RequestableImageSize.s50))
                        ).build(),
                    create("td")
                        .children(
                            Generics.link("/album/" + album.id, album.title)
                        ).build(),
                    create("td")
                        .children(
                            Artists.artistsFromString(album.artists, false)
                        ).build(),
                    create("td")
                        .text(dayFrom(album.release_date))
                        .build(),
                    create("td")
                        .text(album.tracks?.length ?? 0)
                        .build(),
                ).build()
        );
    }

    static albumPage(route: Route, params: any) {
        const album = signal<Album | null>(null);
        const loading = signal(false);
        loading.value = true;
        if (!params.id) {
            const urlParams = new URLSearchParams(window.location.search);
            params.id = urlParams.get("id");
            if (!params.id) {
                return Generics.notFound();
            }
        }
        Api.getAlbum(params.id ?? 0)
            .then(a => album.value = a)
            .finally(() => loading.value = false);

        return Generics.pageFrame(
            create("div")
                .classes("flex-v", "fill-parent")
                .children(
                    ifjs(loading, Generics.loading()),
                    ifjs(album, Albums.album(album)),
                ).build()
        );
    }

    static album(album: Signal<Album | null>) {
        const title = compute(a => a?.title ?? "Album", album);
        const releaseDate = compute(a => {
            return dayFrom(a?.release_date ?? new Date());
        }, album);
        const artists = compute(a => a?.artists ?? "", album);
        const tracks = compute(a => a?.tracks ?? [], album);
        const id = compute(a => a?.id ?? 0, album);
        const src = compute(i => getImageUrl(MediaFileType.albumCover, i, RequestableImageSize.s500), id);
        const firstTrack = compute(t => t.at(0) ?? null, tracks);

        return create("div")
            .classes("flex-v", "fill-parent", "relative")
            .children(
                Generics.image(src, ["background-cover"]),
                create("div")
                    .classes("flex-v", "centered-content")
                    .children(
                        Generics.image(src, ["cover"]),
                        create("div")
                            .classes("flex-v")
                            .children(
                                Generics.heading(1, title, ["bold", "small-line"]),
                                create("div")
                                    .classes("flex")
                                    .children(
                                        create("span")
                                            .classes("artist-name")
                                            .text("Album by")
                                            .build(),
                                        Artists.artistsFromString(artists),
                                    ).build(),
                                Generics.heading(2, "Links"),
                                Tracks.serviceLinks(firstTrack),
                                Generics.heading(2, "Tracks"),
                                Tracks.tracksList(tracks),
                                Generics.date(releaseDate),
                            ).build(),
                    ).build(),
            ).build();
    }
}